import React, { useRef, useState } from "react";
import Modal from "../../components/ui/Modal";
import Icon from "../../components/ui/Icon";
import { downloadTemplate, readInventoryFile } from "../../inventory/sheet";
import { joinList } from "../../inventory/parse";
import { channelKey } from "../../inventory/fields";

const ACCEPT = ".csv,.xlsx,.xls";

export default function ImportDialog({ currentCount, onImport, onClose }) {
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState("");
  const [error, setError] = useState("");
  const [mode, setMode] = useState("merge");
  const [dragging, setDragging] = useState(false);
  const [reading, setReading] = useState(false);
  const inputRef = useRef(null);

  const read = async (file) => {
    if (!file) return;
    setError("");
    setParsed(null);
    setFileName(file.name);
    setReading(true);
    try {
      const result = await readInventoryFile(file);
      if (!result.channels.length) {
        setError("The file has the right columns but no channels under them.");
      } else {
        setParsed(result);
      }
    } catch (e) {
      setError(e.message || "Couldn't read that file. Use a .csv or .xlsx file.");
    } finally {
      setReading(false);
    }
  };

  // Rows for the same channel (partner, name, territory, format) become one
  const channelCount = parsed ? new Set(parsed.channels.map(channelKey)).size : 0;
  const rowCount = parsed ? parsed.channels.length : 0;

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    read(e.dataTransfer.files?.[0]);
  };

  return (
    <Modal
      title="Import channels"
      wide
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-link" onClick={downloadTemplate}>
            Download empty template
          </button>
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={mode === "replace" ? "btn-danger" : "btn-primary"}
            disabled={!parsed}
            onClick={() => onImport(parsed.channels, mode, fileName)}
          >
            {parsed
              ? `${mode === "replace" ? "Replace with" : "Import"} ${channelCount} channels`
              : "Import"}
          </button>
        </>
      }
    >
      <div
        className={`dropzone${dragging ? " is-over" : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && inputRef.current?.click()}
      >
        <Icon name="upload" size={22} />
        <div>
          <strong>{fileName || "Drop a file here or choose one"}</strong>
          <span>
            {reading
              ? "Reading…"
              : "CSV or Excel, laid out like the AdMongrel ad inventory sheet. In Google Sheets: File → Download → .xlsx or .csv."}
          </span>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          hidden
          onChange={(e) => {
            read(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </div>

      {error && <div className="import-error">{error}</div>}

      {parsed && (
        <div className="import-preview">
          <p className="import-summary">
            Found <strong>{channelCount}</strong> channels
            {rowCount > channelCount && (
              <> in {rowCount} rows (rows for the same channel, territory and format are combined)</>
            )}
            {parsed.skipped > 0 && (
              <> · {parsed.skipped} {parsed.skipped === 1 ? "row" : "rows"} skipped (no channel name)</>
            )}
          </p>

          <div className="import-columns">
            {parsed.columns.map((c) => (
              <span key={c.index} className="chip chip-blue" title={`"${c.header}"`}>
                {c.field.label}
              </span>
            ))}
            {parsed.ignored.map((h) => (
              <span key={h} className="chip chip-gray" title="Not imported">
                {h} (ignored)
              </span>
            ))}
          </div>

          <div className="table-wrap import-table">
            <table className="inv-table">
              <thead>
                <tr>
                  <th>Channel name</th>
                  <th>Partner</th>
                  <th>Territory</th>
                  <th>Genre</th>
                  <th>Format</th>
                </tr>
              </thead>
              <tbody>
                {parsed.channels.slice(0, 5).map((c, i) => (
                  <tr key={i}>
                    <td className="cell-strong">{c.channelName}</td>
                    <td>{c.partner}</td>
                    <td>{joinList(c.territories)}</td>
                    <td>{joinList(c.genres)}</td>
                    <td>{joinList(c.formats)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {parsed.channels.length > 5 && (
            <p className="form-hint">and {parsed.channels.length - 5} more</p>
          )}

          <fieldset className="import-mode">
            <legend>How to add them</legend>
            <label className={mode === "merge" ? "is-on" : ""}>
              <input type="radio" name="mode" checked={mode === "merge"} onChange={() => setMode("merge")} />
              <span>
                <strong>Merge</strong>
                Update channels that are already here (same partner and name), add the rest.
              </span>
            </label>
            <label className={mode === "replace" ? "is-on" : ""}>
              <input type="radio" name="mode" checked={mode === "replace"} onChange={() => setMode("replace")} />
              <span>
                <strong>Replace everything</strong>
                Remove the {currentCount} channels here now and keep only the file's.
              </span>
            </label>
          </fieldset>
        </div>
      )}
    </Modal>
  );
}
